// MissionReady Radio — data foundation.
//
// Radio lives INSIDE the existing Messages architecture: a site's Radio thread
// is a `message_channels` row with kind = 'radio' and site_id set, and a
// completed transmission is a normal `messages` row with message_type = 'radio'
// plus transmission metadata in the additive `radio` jsonb column.
//
// Nothing here duplicates messaging, auth, site scoping or Deepgram: site
// authorization is enforced server-side by has_site_access() through the
// ensure_site_radio_channel / post_radio_transmission database functions and
// the existing RLS policies.
import { supabase } from "@/integrations/supabase/client";

export const RADIO_CHANNEL_KIND = "radio";
export const RADIO_MESSAGE_TYPE = "radio";
/** Default logical radio channel inside a site's Radio thread. */
export const RADIO_DEFAULT_CHANNEL = "primary";

export type RadioMetadata = {
  site_id: string | null;
  radio_channel: string | null;
  started_at: string | null;
  ended_at: string | null;
  duration_ms: number | null;
  transcript_source: string | null;
};

export type RadioTransmission = {
  id: number;
  channel_id: string;
  organization_id: string;
  sender_id: string;
  transcript: string;
  created_at: string;
  radio: RadioMetadata | null;
};

export function parseRadioMetadata(value: unknown): RadioMetadata | null {
  if (!value || typeof value !== "object") return null;
  const r = value as Record<string, unknown>;
  return {
    site_id: (r.site_id as string) ?? null,
    radio_channel: (r.radio_channel as string) ?? null,
    started_at: (r.started_at as string) ?? null,
    ended_at: (r.ended_at as string) ?? null,
    duration_ms: typeof r.duration_ms === "number" ? r.duration_ms : Number(r.duration_ms) || null,
    transcript_source: (r.transcript_source as string) ?? null,
  };
}

export function isRadioChannelKind(kind: string | null | undefined): boolean {
  return kind === RADIO_CHANNEL_KIND;
}

/**
 * Find-or-create the Radio thread for an authorized site and join the caller.
 * Authorization is enforced inside the database function.
 */
export async function resolveSiteRadioChannel(orgId: string, siteId: string): Promise<string> {
  const { data, error } = await supabase.rpc("ensure_site_radio_channel", {
    _org: orgId,
    _site: siteId,
  });
  if (error) throw error;
  return data as unknown as string;
}

/**
 * Persist ONE final transcript as a Radio message. Interim transcription
 * results must never be written — only the final transcript is persistent.
 */
export async function postRadioTransmission(args: {
  channelId: string;
  transcript: string;
  startedAt: string;
  endedAt: string;
  radioChannel?: string;
}): Promise<number> {
  const { data, error } = await supabase.rpc("post_radio_transmission", {
    _channel: args.channelId,
    _transcript: args.transcript,
    _started_at: args.startedAt,
    _ended_at: args.endedAt,
    _radio_channel: args.radioChannel ?? RADIO_DEFAULT_CHANNEL,
  });
  if (error) throw error;
  return Number(data);
}

export type RadioSearchResult = RadioTransmission & { site_id: string | null };

/**
 * Search persisted Radio transcripts through the existing messages table.
 * RLS restricts results to Radio threads the caller is authorized for.
 *
 * `senderIds` narrows by personnel (resolved from profiles by the caller),
 * `from`/`to` narrow by date or date range.
 */
export async function searchRadioTransmissions(args: {
  orgId: string;
  text?: string;
  senderIds?: string[];
  from?: string;
  to?: string;
  channelId?: string;
  limit?: number;
}): Promise<RadioSearchResult[]> {
  let q = supabase
    .from("messages")
    .select("id, channel_id, organization_id, sender_id, body, created_at, radio")
    .eq("organization_id", args.orgId)
    .eq("message_type", RADIO_MESSAGE_TYPE)
    .order("created_at", { ascending: false })
    .limit(args.limit ?? 50);

  if (args.text?.trim()) q = q.ilike("body", `%${args.text.trim()}%`);
  if (args.senderIds?.length) q = q.in("sender_id", args.senderIds);
  if (args.channelId) q = q.eq("channel_id", args.channelId);
  if (args.from) q = q.gte("created_at", args.from);
  if (args.to) q = q.lte("created_at", args.to);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((m: any) => {
    const radio = parseRadioMetadata(m.radio);
    return {
      id: m.id,
      channel_id: m.channel_id,
      organization_id: m.organization_id,
      sender_id: m.sender_id,
      transcript: m.body,
      created_at: m.created_at,
      radio,
      site_id: radio?.site_id ?? null,
    };
  });
}

// ------------------------------------------------------------------
// Realtime foundation (signaling / presence only).
//
// Reuses the existing Supabase Realtime connection — no second WebSocket
// system, no TURN, no SFU, no video, no 1:1 calling. WebRTC audio transport
// and the Deepgram streaming leg are implemented in a later step and will
// exchange their control messages on these topics.
// ------------------------------------------------------------------

/** Control/signaling topic for a site's Radio thread. */
export function radioSignalTopic(channelId: string): string {
  return `radio:signal:${channelId}`;
}

/** Presence topic (who is on the channel / who is transmitting). */
export function radioPresenceTopic(channelId: string): string {
  return `radio:presence:${channelId}`;
}

export type RadioSignalEvent =
  | { type: "transmit-start"; userId: string; at: string }
  | { type: "transmit-end"; userId: string; at: string }
  | { type: "webrtc-offer"; userId: string; sdp: string }
  | { type: "webrtc-answer"; userId: string; sdp: string }
  | { type: "webrtc-ice"; userId: string; candidate: unknown };

export const RADIO_SIGNAL_EVENT = "radio-signal";
